import type { OrderState } from 'Util/OrderState.js'
import type { IPipelineState, ReducedTaskInstance } from '../../../Model/Order/IPipelineState.js'
import { AirflowErrorStatesSet, AirflowProgressStatesSet } from '../../../Model/Order/IPipelineState.js'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'

export function orderToStateWidget(state: OrderState, pipelineState: IPipelineState | null) {
  const allwidgetClass = 'ml-2 inline-flex items-center bg-blue-500 text-white text-sm font-semibold px-3 py-1 rounded-full' //'rounded-md text-xl pl-2 pr-2 pb-1 pt-1 ml-3'
  switch (state) {
    case 'DONE':
      return (
        <TooltipTrigger classNames={'inline-flex'} tooltip={'Order analysis has finished.'}>
          <span className={`${allwidgetClass} bg-primary`}>Done</span>
        </TooltipTrigger>
      )
    case 'DELETED':
      return (
        <TooltipTrigger classNames={'inline-flex'} tooltip={'Order has been deleted and is read only'}>
          <span className={`${allwidgetClass} bg-red-700`}>Deleted</span>
        </TooltipTrigger>
      )
    case 'PREPROCESSED_BLOCKED':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={
            'Processing yielded error or insufficient quality. Check the quality screen to exclude bad samples from postprocessing or consider using Manage -> Change Data for resubmission to adjust your primers.'
          }
        >
          <span className={`${allwidgetClass} bg-red-700`}>Postprocessing Blocked</span>
        </TooltipTrigger>
      )
    case 'READY_FOR_POSTPROCESSING':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={'Preprocessing complete. Review the results and start postprocessing when ready.'}
        >
          <span className={`${allwidgetClass} bg-emerald-500`}>Ready for Postprocessing</span>
        </TooltipTrigger>
      )
    case 'POSTPROCESSING':
      return orderPostprocessingWidget(allwidgetClass, pipelineState)
    case 'READY_FOR_PREPROCESSING':
      return (
        <TooltipTrigger classNames={'inline-flex'} tooltip={'All necessary information has been gathered to start preprocessing.'}>
          <span className={`${allwidgetClass} bg-cyan-400`}>Ready for Processing</span>
        </TooltipTrigger>
      )
    case 'PREPROCESSING':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={
            'Your order is currently being preprocessed, gathering information about the separate samples. Check progress on the order quality screen.'
          }
        >
          <span className={`${allwidgetClass} bg-blue-400`}>Preprocessing Items</span>
        </TooltipTrigger>
      )
    case 'PENDING':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={
            'Your order information is incomplete. First, upload samples and negative controls into pools and then upload your sample sheet to assign primers to the created samples.'
          }
        >
          <span className={`${allwidgetClass} bg-purple-400`}>Pending</span>
        </TooltipTrigger>
      )
    case 'DRAFT':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={
            'Order has been created by customer and is waiting for a lab to accept the order. Consider issuing an invitation to a lab via Sharing & Access -> Invite Lab'
          }
        >
          <span className={`${allwidgetClass} bg-gray-400`}>Draft - waiting for Lab</span>
        </TooltipTrigger>
      )
    /* The order's own fields do not settle the state and its pools have not arrived. Saying nothing
       definite beats the old guess of "Pending", which claimed the lab was still working on orders
       that were in fact preprocessed, or blocked. */
    case 'UNKNOWN':
      return (
        <TooltipTrigger classNames={'inline-flex'} tooltip={'Working out where this order stands.'}>
          <span className={`${allwidgetClass} bg-gray-300 text-gray-700`}>…</span>
        </TooltipTrigger>
      )
  }
}

function orderPostprocessingWidget(allwidgetClass: string, pipelineState: IPipelineState | null) {
  switch (pipelineState?.type) {
    case null:
    case 'NONE':
    case 'COMPLETED':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={'Order is currently postprocessing. This might take a while, you will receive an e-mail notification upon completion.'}
        >
          <span className={`${allwidgetClass} bg-blue-400`}>Postprocessing Order</span>
        </TooltipTrigger>
      )
    case 'ERROR':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={'An error occurred during postprocessing. Consider contacting the spcfy team for more details.'}
        >
          <span className={`${allwidgetClass} bg-red-900`}>Postprocessing Error</span>
          {assembleOrderTasks(pipelineState.tasks ?? [])}
        </TooltipTrigger>
      )
    case 'PROGRESS':
      return (
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={'Order is currently postprocessing. This might take a while, you will receive an e-mail notification upon completion.'}
        >
          <span className={`${allwidgetClass} bg-blue-400`}>Postprocessing Order</span>
          {assembleOrderTasks(pipelineState.tasks ?? [])}
        </TooltipTrigger>
      )
  }
}

function assembleOrderTasks(tasks: ReducedTaskInstance[]) {
  return tasks
    .filter((x) => AirflowErrorStatesSet.find((y) => y == x.state) || AirflowProgressStatesSet.find((y) => y == x.state))
    .map((x) => {
      if (AirflowErrorStatesSet.find((y) => y == x.state)) {
        return (
          <span key={x.taskName} className="p-2 text-red-800 text-xs">
            Task {x.taskName} {assembleTime(x.duration)}&nbsp;
            <span className="text-red-700 text-xs font-bold">ERROR</span>
          </span>
        )
      } else {
        return (
          <span key={x.taskName} className="p-2 text-xs flex gap-2">
            Task {x.taskName} {assembleTime(x.duration)}&nbsp;
            <span className="loading loading-spinner loading-xs" />
          </span>
        )
      }
    })
}

function assembleTime(duration: number | undefined): string {
  const rounded = Math.round(duration ?? -1)
  if (rounded <= 0) {
    return ''
  } else {
    return `(${rounded}ms)`
  }
}
